import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";

import PDFApi from "api/pdfApi";
import BucketApi from "api/bucketApi";
import ProcesosApi from "api/procesosApi";

const ReprogramarAudiencia = ({
  open,
  onClose,
  audienciaId,
  audiencia: audienciaProp,
  onSuccess,
}) => {
  const [error, setError] = useState(undefined);
  const [audiencia, setAudiencia] = useState(null);
  const [newReprogramacion, setNewReprogramacion] = useState({
    id_audiencia: audienciaId || null,
    nueva_fecha: null,
    nueva_hora: null,
    observacion: null,
  });
  const [saving, setSaving] = useState(false);

  const fetchInitialData = async () => {
    try {
      // Si se pasa la audiencia como prop, usarla directamente
      if (audienciaProp) {
        setAudiencia(audienciaProp);
        return;
      }
      // Solo hacer fetch si no se pasa la audiencia como prop
      const audienciaResp = await ProcesosApi.fetchAudienciaById(audienciaId);
      setAudiencia(audienciaResp.data);
    } catch (error) {
      console.error("Error al obtener los datos iniciales:", error);
      setError(obtenerMensajeError(error, "Error al cargar los datos iniciales."));
    }
  };

  useEffect(() => {
    if (open && (audienciaId || audienciaProp)) {
      fetchInitialData();
    }
  }, [audienciaId, audienciaProp, open]);

  // Actualizar audiencia local cuando cambie la prop
  useEffect(() => {
    if (audienciaProp) {
      setAudiencia(audienciaProp);
    }
  }, [audienciaProp]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newReprogramacion.nueva_fecha) {
      alert("Debe seleccionar una Fecha para la Reprogramación.");
      return;
    }
    if (!newReprogramacion.nueva_hora) {
      alert("Debe ingresar una Hora para la Reprogramación.");
      return;
    }
    if (!newReprogramacion.observacion) {
      alert("Debe ingresar una Observación.");
      return;
    }

    const etapas = audiencia?.etapas || [];
    const etapasActualizadas = etapas.map((etapa) => {
      // Etapa Quorum
      if (etapa.orden === 1) {
        return {
          ...etapa,
          resultado: {
            ...etapa.resultado,
            resultado: "REPROGRAMADA",
            observacion_acta: newReprogramacion.observacion,
          },
        };
      }
      return etapa;
    });
    const audienciaActualizada = {
      ...audiencia,
      etapas: etapasActualizadas,
    };
    const confirmacion = window.confirm(
      `¿Está seguro(a) de que desea reprogramar una nueva Audiencia?`
    );
    if (!confirmacion) return;
    try {
      setSaving(true);
      console.log("Guardando audiencia actualizada:", audienciaActualizada);
      await ProcesosApi.actualizarAudiencia(audiencia?.id, audienciaActualizada);
      const datosSuspension = {
        operacion: "REPROGRAMAR_AUDIENCIA",
        id_audiencia: audienciaId,
        nueva_fecha: newReprogramacion.nueva_fecha,
        nueva_hora: newReprogramacion.nueva_hora,
        observacion_acta: newReprogramacion.observacion,
      };
      const response = await ProcesosApi.operarAudiencia(datosSuspension);
      if (response.status === 201 && response.data) {
        const FILE_NAME = `audiencia_${audiencia?.consecutivo}.pdf`;
        const ANEXO_TYPE = `AUDIENCIA_${audiencia?.consecutivo}`;
        const anexoResponse = await ProcesosApi.obtenerAnexoProcesoByTipo(audiencia?.proceso, {
          tipo_anexo: ANEXO_TYPE,
        });
        const anexo = anexoResponse.data;
        if (anexo != undefined && !anexoResponse.data.error) {
          await ProcesosApi.eliminarAnexoProceso(audiencia?.proceso, anexo.id);
        }
        const pdfResponse = await PDFApi.generateDocument(
          "suspensionReprogramacion",
          audiencia?.id
        );
        const pdfBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
        const formData = new FormData();
        formData.append("file", pdfBlob, FILE_NAME);
        formData.append("file_type", "audiencias");
        formData.append("proceso_id", audiencia?.proceso_identificador);
        formData.append("registrar_anexo", "true");
        formData.append("tipo_anexo", ANEXO_TYPE);
        formData.append("nombre_anexo", "Acta Reprogramación");
        await BucketApi.uploadFile(formData);
      }
      // Limpiar el formulario
      setNewReprogramacion({
        id_audiencia: audienciaId,
        nueva_fecha: null,
        nueva_hora: null,
        observacion: null,
      });
      alert("La Audiencia ha sido reprogramada correctamente.");
      onSuccess();
    } catch (error) {
      console.error("Error en al Reprogramar la Audiencia:", error);
      alert(obtenerMensajeError(error, "Ocurrió un error al Reprogramar la Audiencia."));
    } finally {
      setSaving(false);
    }
  };

  const hCClose = () => {
    // Limpiar el formulario al cerrar
    setNewReprogramacion({
      id_audiencia: audienciaId,
      nueva_fecha: null,
      nueva_hora: null,
      observacion: null,
    });
    setError(undefined);
    onClose();
  };

  return (
    <>
      {saving && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Dialog open={open} onClose={hCClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <SuiBox mt={1}>
            <strong>
              Reprogramar Audiencia del Proceso:{" "}
              {audiencia?.proceso_identificador || "No tiene Proceso"}
            </strong>
          </SuiBox>
        </DialogTitle>
        <DialogContent>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Grid>
          </Grid>

          {/* Debug info - remover en producción */}
          {/* {process.env.NODE_ENV === "development" && audiencia && (
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 2 }}>
              <Grid size={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Debug Info:</strong>
                  <br />
                  Audiencia ID: {audiencia.id}
                  <br />
                  Estado Quorum: {audiencia.etapas?.find((e) => e.orden === 1)?.estado}
                  <br />
                  Deudor asistió:{" "}
                  {audiencia.etapas?.find((e) => e.orden === 1)?.participantes?.deudor?.asistio
                    ? "SÍ"
                    : "NO"}
                  <br />
                  Apoderado asistió:{" "}
                  {audiencia.etapas?.find((e) => e.orden === 1)?.participantes?.deudor_apoderado
                    ?.asistio
                    ? "SÍ"
                    : "NO"}
                </Alert>
              </Grid>
            </Grid>
          )} */}

          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 2 }}>
            <Grid size={12}>
              <SuiTypography variant="h6" fontWeight="light">
                Fecha y Hora
              </SuiTypography>
            </Grid>
            <Grid size={6}>
              <SuiInput
                fullWidth
                type="date"
                value={newReprogramacion.nueva_fecha || ""}
                onChange={(e) =>
                  setNewReprogramacion({ ...newReprogramacion, nueva_fecha: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={6}>
              <SuiInput
                fullWidth
                type="time"
                value={newReprogramacion.nueva_hora || ""}
                onChange={(e) =>
                  setNewReprogramacion({ ...newReprogramacion, nueva_hora: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              <SuiTypography variant="h6" fontWeight="light">
                Observación de la Reprogramación
              </SuiTypography>
              <SuiInput
                name="observacion"
                multiline
                rows={4}
                value={newReprogramacion.observacion || ""}
                onChange={(e) =>
                  setNewReprogramacion({ ...newReprogramacion, observacion: e.target.value })
                }
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SuiButton onClick={hCClose} color="secondary">
            Cancelar
          </SuiButton>
          <SuiButton variant="gradient" buttonColor="dark" onClick={handleSubmit} disabled={saving}>
            {saving ? "Reprogramando..." : "Reprogramar"}
          </SuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

ReprogramarAudiencia.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  audienciaId: PropTypes.number,
  audiencia: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
};

export default ReprogramarAudiencia;
